import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { PresentationWidget, Subcontractor, WeeklyPlan, EvaluationParameter } from '../../types';
import { WeeklyPlanTableWidget, WeeklyPlanDataProps } from './WeeklyPlanTableWidget';
import { GraphicProgressPlanningView } from '../graphic-progress/GraphicProgressPlanningView';
import { calculateMultiSCurve, truncateProgress } from '../../utils/planningUtils';
import { DEFAULT_EVALUATION_RANGES, EvaluationRangeBadge, getEvaluationRangeForScore } from '../payments/EvaluationConfigModal';
import { LocalAnalyticsContainer } from '../analytics/LocalAnalyticsContainer';

interface WidgetRendererProps {
    widget: PresentationWidget;
    isSelected: boolean;
    weeklyPlanData?: WeeklyPlanDataProps;
    onSplitWidgetIntoSlides?: (widgetId: string) => void;
    projectId?: string;
    onUpdateConfig?: (widgetId: string, config: Record<string, any>) => void;
}

/**
 * Renders the inner content of a presentation widget based on its type.
 * Each widget type renders representative visual content.
 */
const WidgetRenderer: React.FC<WidgetRendererProps> = ({
    widget,
    isSelected,
    weeklyPlanData,
    onSplitWidgetIntoSlides,
    projectId,
    onUpdateConfig
}) => {
    const baseStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: '8px',
        fontSize: '11px',
        display: 'flex',
        flexDirection: 'column',
    };

    const headerStyle = (color: string): React.CSSProperties => ({
        background: color,
        color: '#fff',
        padding: '6px 10px',
        fontSize: '11px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
    });

    const bodyStyle: React.CSSProperties = {
        flex: 1,
        padding: '8px 10px',
        overflow: 'hidden',
        background: '#fff',
    };

    switch (widget.type) {
        // --- Maestra & Intermedia ---
        case 'gantt_master':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #1e40af, #3b82f6)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>calendar_month</span>
                        Carta Gantt Maestra
                    </div>
                    <div style={bodyStyle}>
                        <GanttMockChart />
                    </div>
                </div>
            );
        case 's_curve':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #1e40af, #2563eb)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>trending_up</span>
                        Curva S de Avance (Programado vs Real)
                    </div>
                    <div style={{ flex: 1, padding: '10px', background: '#fff', overflow: 'hidden' }}>
                        <SCurvePresentationWidget weeklyPlanData={weeklyPlanData} />
                    </div>
                </div>
            );
        case 'milestones':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #7c3aed, #a78bfa)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>flag</span>
                        Hitos del Proyecto
                    </div>
                    <div style={bodyStyle}>
                        <MilestonesMock />
                    </div>
                </div>
            );
        case 'lookahead':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #0e7490, #22d3ee)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>view_timeline</span>
                        Lookahead
                    </div>
                    <div style={bodyStyle}>
                        <LookaheadMock />
                    </div>
                </div>
            );

        // --- Semanal ---
        case 'weekly_prev_full':
        case 'weekly_prev_executed':
        case 'weekly_prev_not_executed':
        case 'weekly_current':
            return (
                <div style={baseStyle}>
                    <WeeklyPlanTableWidget
                        widget={widget}
                        data={weeklyPlanData}
                        isSelected={isSelected}
                        onSplitIntoSlides={onSplitWidgetIntoSlides ? () => onSplitWidgetIntoSlides(widget.id) : undefined}
                    />
                </div>
            );
        case 'weekly_prev_cnc':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #ea580c, #fb923c)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>report_problem</span>
                        Causas de No Cumplimiento
                    </div>
                    <div style={bodyStyle}><CNCMock /></div>
                </div>
            );
        case 'graphic_progress_current':
        case 'graphic_progress_prev': {
            const isCurrent = widget.type === 'graphic_progress_current';
            const sortedPlans = weeklyPlanData?.weeklyPlans && weeklyPlanData.weeklyPlans.length > 0
                ? [...weeklyPlanData.weeklyPlans].sort((a, b) => a.id - b.id)
                : [];

            let targetPlanId: number | null = null;
            if (isCurrent) {
                targetPlanId = weeklyPlanData?.activePlanId
                    || sortedPlans.find(p => p.status !== 'Cerrada')?.id
                    || (sortedPlans.length > 0 ? sortedPlans[sortedPlans.length - 1].id : null);
            } else {
                if (weeklyPlanData?.activePlanId) {
                    const cIdx = sortedPlans.findIndex(p => p.id === weeklyPlanData.activePlanId);
                    if (cIdx > 0) targetPlanId = sortedPlans[cIdx - 1].id;
                }
                if (!targetPlanId) {
                    targetPlanId = sortedPlans.length >= 2
                        ? sortedPlans[sortedPlans.length - 2].id
                        : (sortedPlans.length > 0 ? sortedPlans[0].id : null);
                }
            }

            const safeSavedProtocolsRecord = React.useMemo(() => {
                const rec: Record<string, any> = {};
                if (Array.isArray(weeklyPlanData?.savedProtocols)) {
                    weeklyPlanData?.savedProtocols.forEach(p => {
                        if (p && p.id) rec[p.id] = p;
                    });
                }
                return rec;
            }, [weeklyPlanData?.savedProtocols]);

            return (
                <div style={baseStyle}>
                    <div style={headerStyle(isCurrent ? 'linear-gradient(135deg, #059669, #34d399)' : 'linear-gradient(135deg, #4338ca, #818cf8)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>map</span>
                        {isCurrent ? 'Avance Gráfico (Semana Actual)' : 'Avance Gráfico (Semana Anterior)'}
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                        {weeklyPlanData?.projectInfo?.id ? (
                            <GraphicProgressPlanningView
                                activeProjectId={weeklyPlanData.projectInfo.id}
                                tasks={weeklyPlanData.tasks || []}
                                units={weeklyPlanData.units || []}
                                levels={weeklyPlanData.levels || []}
                                matrixData={weeklyPlanData.matrixData || {}}
                                protocolConfigs={weeklyPlanData.protocolConfigs || {}}
                                projectInfo={weeklyPlanData.projectInfo}
                                savedProtocols={safeSavedProtocolsRecord}
                                weeklyPlans={weeklyPlanData.weeklyPlans || []}
                                responsibles={weeklyPlanData.responsibles || []}
                                activePlanId={targetPlanId}
                            />
                        ) : (
                            <div style={{ ...bodyStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <GraphicProgressMock label={isCurrent ? 'Actual' : 'Anterior'} />
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // --- Calidad & Subcontratos ---
        case 'protocol_summary':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #7e22ce, #c084fc)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>fact_check</span>
                        Resumen de Protocolos
                    </div>
                    <div style={bodyStyle}><ProtocolSummaryMock /></div>
                </div>
            );
        case 'compliance_semaphore':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #b45309, #fbbf24)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>traffic</span>
                        Semáforos de Cumplimiento
                    </div>
                    <div style={bodyStyle}><SemaphoreMock /></div>
                </div>
            );
        case 'sc_evaluations':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #0f766e, #0d9488)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>assessment</span>
                        Evaluación de Subcontratos (SC)
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>
                        <SCEvaluationPresentationWidget weeklyPlanData={weeklyPlanData} />
                    </div>
                </div>
            );

        // --- Reportes ---
        case 'pareto_chart':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #be123c, #fb7185)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>bar_chart</span>
                        Gráfico Pareto CNC
                    </div>
                    <div style={bodyStyle}><ParetoMock /></div>
                </div>
            );
        case 'ppc_evolution':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #0284c7, #7dd3fc)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>trending_up</span>
                        Evolución PPC / PCR
                    </div>
                    <div style={bodyStyle}><PPCEvolutionMock /></div>
                </div>
            );
        case 'kpi_cards':
            return (
                <div style={baseStyle}>
                    <div style={headerStyle('linear-gradient(135deg, #4f46e5, #a5b4fc)')}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>dashboard</span>
                        Tarjetas KPIs
                    </div>
                    <div style={bodyStyle}><KPICardsMock /></div>
                </div>
            );
        case 'report_template':
            return (
                <div style={{ ...baseStyle, background: '#ffffff', padding: 0, overflow: 'hidden', borderRadius: '8px' }}>
                    {projectId ? (
                        <LocalAnalyticsContainer
                            context="consumption"
                            action="view"
                            initialMode="view"
                            canView={true}
                            projectId={projectId}
                            initialWidgets={widget.config?.widgets || []}
                            initialName={widget.config?.templateName}
                            isSlideEmbed={true}
                        />
                    ) : (
                        <ReportTemplateSlideView config={widget.config} />
                    )}
                </div>
            );

        // --- Generic & PowerPoint Style Design Blocks ---
        case 'title_block': {
            const text = widget.config?.text !== undefined ? widget.config.text : 'Título de la Diapositiva';
            const subtitle = widget.config?.subtitle;
            const fontSize = widget.config?.fontSize || 26;
            const color = widget.config?.color || '#0f172a';
            const fontFamily = widget.config?.fontFamily || "'Inter', sans-serif";
            const fontWeight = widget.config?.fontWeight || '800';
            const fontStyle = widget.config?.fontStyle || 'normal';
            const textDecoration = widget.config?.textDecoration || 'none';
            const textAlign = widget.config?.textAlign || 'left';
            const bg = widget.config?.background || 'transparent';
            const borderBottom = widget.config?.showDivider ? `2px solid ${color}` : 'none';

            return (
                <div style={{
                    ...baseStyle,
                    background: bg,
                    padding: '8px 12px',
                    justifyContent: 'center',
                    borderBottom,
                    borderRadius: widget.config?.borderRadius || '0px',
                }}>
                    <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={e => onUpdateConfig && onUpdateConfig(widget.id, { text: e.currentTarget.textContent || '' })}
                        style={{
                            color,
                            fontSize: `${fontSize}px`,
                            fontFamily,
                            fontWeight,
                            fontStyle,
                            textDecoration,
                            textAlign,
                            lineHeight: 1.2,
                            outline: 'none',
                            cursor: 'text',
                        }}
                    >
                        {text}
                    </div>
                    {subtitle && (
                        <div style={{ fontSize: `${Math.max(11, fontSize * 0.45)}px`, color: '#64748b', textAlign, marginTop: '4px' }}>
                            {subtitle}
                        </div>
                    )}
                </div>
            );
        }

        case 'text_block': {
            const text = widget.config?.text !== undefined ? widget.config.text : 'Haz doble clic o escribe aquí tus notas, observaciones o resumen ejecutivo...';
            const fontSize = widget.config?.fontSize || 13;
            const color = widget.config?.color || '#334155';
            const fontFamily = widget.config?.fontFamily || "'Inter', sans-serif";
            const fontWeight = widget.config?.fontWeight || '400';
            const fontStyle = widget.config?.fontStyle || 'normal';
            const textDecoration = widget.config?.textDecoration || 'none';
            const textAlign = widget.config?.textAlign || 'left';
            const bg = widget.config?.background || '#ffffff';
            const padding = widget.config?.padding || 12;
            const borderRadius = widget.config?.borderRadius !== undefined ? widget.config.borderRadius : 8;
            const border = widget.config?.border || '1px solid #e2e8f0';

            return (
                <div style={{
                    ...baseStyle,
                    background: bg,
                    padding: `${padding}px`,
                    borderRadius: `${borderRadius}px`,
                    border,
                    justifyContent: 'flex-start',
                    boxShadow: widget.config?.shadow ? '0 4px 12px rgba(0,0,0,0.06)' : 'none',
                }}>
                    <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={e => onUpdateConfig && onUpdateConfig(widget.id, { text: e.currentTarget.innerText || '' })}
                        style={{
                            color,
                            fontSize: `${fontSize}px`,
                            fontFamily,
                            fontWeight,
                            fontStyle,
                            textDecoration,
                            textAlign,
                            lineHeight: 1.5,
                            outline: 'none',
                            cursor: 'text',
                            whiteSpace: 'pre-wrap',
                            width: '100%',
                            height: '100%',
                            overflowY: 'auto',
                        }}
                    >
                        {text}
                    </div>
                </div>
            );
        }

        case 'image_block': {
            const src = widget.config?.src;
            const objectFit = widget.config?.objectFit || 'contain';
            const borderRadius = widget.config?.borderRadius !== undefined ? widget.config.borderRadius : 8;
            const caption = widget.config?.caption;

            const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (event) => {
                    const base64 = event.target?.result as string;
                    if (base64 && onUpdateConfig) {
                        onUpdateConfig(widget.id, { src: base64 });
                    }
                };
                reader.readAsDataURL(file);
            };

            return (
                <div style={{
                    ...baseStyle,
                    background: src ? 'transparent' : '#f8fafc',
                    borderRadius: `${borderRadius}px`,
                    border: src ? 'none' : '2px dashed #cbd5e1',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {src ? (
                        <>
                            <img
                                src={src}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit, borderRadius: `${borderRadius}px` }}
                            />
                            {isSelected && (
                                <label
                                    style={{
                                        position: 'absolute',
                                        bottom: '6px',
                                        right: '6px',
                                        background: 'rgba(15, 23, 42, 0.75)',
                                        color: '#fff',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '10px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        backdropFilter: 'blur(4px)',
                                        zIndex: 2,
                                    }}
                                    title="Cambiar imagen"
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>upload</span>
                                    Cambiar
                                    <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
                                </label>
                            )}
                        </>
                    ) : (
                        <label style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: '#94a3b8',
                            padding: '16px',
                            textAlign: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '36px', color: '#3b82f6', marginBottom: '6px' }}>add_photo_alternate</span>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#475569' }}>Cargar Imagen o Logotipo</div>
                            <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>PNG, JPG, SVG o WebP</div>
                            <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
                        </label>
                    )}
                    {caption && (
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            fontSize: '9px',
                            padding: '2px 6px',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {caption}
                        </div>
                    )}
                </div>
            );
        }

        case 'shape_block': {
            const shapeType = widget.config?.shapeType || 'rectangle'; // 'rectangle' | 'circle' | 'pill' | 'card' | 'line'
            const fillColor = widget.config?.fillColor || '#2563eb';
            const borderColor = widget.config?.borderColor || 'transparent';
            const borderWidth = widget.config?.borderWidth !== undefined ? widget.config.borderWidth : 0;
            const opacity = widget.config?.opacity !== undefined ? widget.config.opacity : 1;
            const shadow = widget.config?.shadow;
            const textInside = widget.config?.textInside;
            const textColor = widget.config?.textColor || '#ffffff';
            const fontSize = widget.config?.fontSize || 12;

            if (shapeType === 'line') {
                return (
                    <div style={{
                        ...baseStyle,
                        background: 'transparent',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4px',
                    }}>
                        <div style={{
                            width: '100%',
                            height: `${Math.max(2, borderWidth || 3)}px`,
                            background: fillColor,
                            opacity,
                            borderRadius: '2px'
                        }} />
                    </div>
                );
            }

            let borderRadius = '8px';
            if (shapeType === 'circle') borderRadius = '50%';
            if (shapeType === 'pill') borderRadius = '9999px';
            if (shapeType === 'rectangle') borderRadius = `${widget.config?.borderRadius ?? 6}px`;

            return (
                <div style={{
                    ...baseStyle,
                    background: fillColor,
                    border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none',
                    borderRadius,
                    opacity,
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: shadow ? '0 10px 25px -5px rgba(0,0,0,0.15)' : 'none',
                    padding: '8px',
                    color: textColor,
                    fontSize: `${fontSize}px`,
                    fontWeight: 700,
                    textAlign: 'center',
                }}>
                    {textInside ? (
                        <div
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={e => onUpdateConfig && onUpdateConfig(widget.id, { textInside: e.currentTarget.textContent || '' })}
                            style={{ outline: 'none', cursor: 'text' }}
                        >
                            {textInside}
                        </div>
                    ) : null}
                </div>
            );
        }

        default:
            return (
                <div style={{ ...baseStyle, background: '#f8fafc', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>widgets</span>
                    <span style={{ fontSize: '10px', marginTop: '4px' }}>{widget.type}</span>
                </div>
            );
    }
};

// ========== MOCK SUB-COMPONENTS ==========

const GanttMockChart = () => {
    const bars = [
        { name: 'Obra Gruesa', w: '70%', color: '#3b82f6', left: '5%' },
        { name: 'Terminaciones', w: '45%', color: '#8b5cf6', left: '35%' },
        { name: 'Inst. Sanitarias', w: '55%', color: '#06b6d4', left: '15%' },
        { name: 'Inst. Eléctricas', w: '40%', color: '#f59e0b', left: '25%' },
    ];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', height: '100%', justifyContent: 'center' }}>
            {bars.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '9px', color: '#64748b', width: '80px', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                    <div style={{ flex: 1, position: 'relative', height: '12px', background: '#f1f5f9', borderRadius: '3px' }}>
                        <div style={{ position: 'absolute', left: b.left, width: b.w, height: '100%', background: b.color, borderRadius: '3px', opacity: 0.85 }} />
                    </div>
                </div>
            ))}
        </div>
    );
};

const MilestonesMock = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', height: '100%', justifyContent: 'center' }}>
        {['Inicio Obra Gruesa', 'Entrega Piso 1-5', 'Término Fachada', 'Recepción Municipal'].map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: i < 2 ? '#22c55e' : i === 2 ? '#f59e0b' : '#94a3b8', flexShrink: 0 }} />
                <span style={{ fontSize: '10px', color: '#334155', flex: 1 }}>{m}</span>
                <span style={{ fontSize: '9px', color: '#94a3b8' }}>{`${15 + i * 3}/0${3 + i}/26`}</span>
            </div>
        ))}
    </div>
);

const LookaheadMock = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '3px' }}>
        {['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4'].map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '9px', color: '#64748b', width: '36px' }}>{s}</span>
                <div style={{ flex: 1, height: '10px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ width: `${90 - i * 15}%`, height: '100%', background: `hsl(${200 + i * 25}, 70%, 55%)`, borderRadius: '2px' }} />
                </div>
                <span style={{ fontSize: '9px', color: '#64748b', width: '24px' }}>{`${90 - i * 15}%`}</span>
            </div>
        ))}
    </div>
);

const WeeklyTableMock = ({ label, color }: { label: string; color: string }) => (
    <div style={{ height: '100%', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
            <thead>
                <tr style={{ background: `${color}15` }}>
                    <th style={{ padding: '3px 4px', textAlign: 'left', fontWeight: 600, color, borderBottom: `1px solid ${color}30` }}>Tarea</th>
                    <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color, borderBottom: `1px solid ${color}30` }}>UU.FF</th>
                    <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color, borderBottom: `1px solid ${color}30` }}>%</th>
                </tr>
            </thead>
            <tbody>
                {['Enfierradura', 'Hormigonado', 'Tabiquería'].map((t, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '2px 4px', color: '#475569' }}>{t}</td>
                        <td style={{ padding: '2px 4px', textAlign: 'center', color: '#64748b' }}>{`Mz${i + 1}`}</td>
                        <td style={{ padding: '2px 4px', textAlign: 'center', color: '#334155', fontWeight: 600 }}>{`${70 + i * 10}%`}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const CNCMock = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '3px', justifyContent: 'center' }}>
        {[
            { cause: 'Material no disponible', qty: 4, pct: '40%' },
            { cause: 'Cambio de diseño', qty: 3, pct: '30%' },
            { cause: 'Clima adverso', qty: 2, pct: '20%' },
            { cause: 'Subcontrato', qty: 1, pct: '10%' },
        ].map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '9px', color: '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.cause}</span>
                <div style={{ width: '50px', height: '8px', background: '#fee2e2', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ width: c.pct, height: '100%', background: '#ef4444', borderRadius: '2px' }} />
                </div>
                <span style={{ fontSize: '9px', color: '#94a3b8', width: '14px', textAlign: 'right' }}>{c.qty}</span>
            </div>
        ))}
    </div>
);

const GraphicProgressMock = ({ label }: { label: string }) => (
    <div style={{ textAlign: 'center', color: '#64748b' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3px', padding: '4px' }}>
            {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} style={{
                    width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #e2e8f0',
                    background: i < 5 ? `hsl(${140 + i * 10}, 60%, ${85 - i * 8}%)` : '#f8fafc',
                }} />
            ))}
        </div>
        <div style={{ fontSize: '9px', marginTop: '4px' }}>Avance Gráfico — {label}</div>
    </div>
);

const ProtocolSummaryMock = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '3px', justifyContent: 'center' }}>
        {[
            { name: 'Abiertos', count: 12, color: '#3b82f6' },
            { name: 'Cerrados', count: 34, color: '#22c55e' },
            { name: 'Rechazados', count: 3, color: '#ef4444' },
        ].map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                <span style={{ fontSize: '10px', color: '#475569', flex: 1 }}>{p.name}</span>
                <span style={{ fontSize: '11px', fontWeight: 700, color: p.color }}>{p.count}</span>
            </div>
        ))}
    </div>
);

const SemaphoreMock = () => (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
        {[
            { color: '#22c55e', label: 'OK', size: 28 },
            { color: '#f59e0b', label: 'Alerta', size: 22 },
            { color: '#ef4444', label: 'Crítico', size: 18 },
        ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ width: s.size, height: s.size, borderRadius: '50%', background: s.color, margin: '0 auto', boxShadow: `0 0 8px ${s.color}40` }} />
                <div style={{ fontSize: '8px', color: '#64748b', marginTop: '3px' }}>{s.label}</div>
            </div>
        ))}
    </div>
);

// =========================================================================
// WIDGET REAL: CURVA S DE AVANCE (PROGRAMADO VS REAL)
// =========================================================================
export const SCurvePresentationWidget: React.FC<{ weeklyPlanData?: WeeklyPlanDataProps }> = ({ weeklyPlanData }) => {
    const tasks = weeklyPlanData?.tasks || [];
    const baselines = (weeklyPlanData?.baselines || []) as any[];
    const progressHistory = (weeklyPlanData?.progressHistory || []) as any[];

    // Calculate real multi-S curve data based on project tasks, baselines, and historical progress
    const curveData = React.useMemo(() => {
        if (!tasks || tasks.length === 0) {
            // Mock fallback if no tasks are available
            return [
                { name: 'S1', planActive: 10, real: 12 },
                { name: 'S2', planActive: 25, real: 28 },
                { name: 'S3', planActive: 45, real: 42 },
                { name: 'S4', planActive: 65, real: 60 },
                { name: 'S5', planActive: 80, real: null },
                { name: 'S6', planActive: 100, real: null },
            ];
        }
        try {
            const virtualHistory = [...progressHistory];
            return calculateMultiSCurve(tasks, baselines, virtualHistory, 5);
        } catch (e) {
            console.error('Error computing Curva S in widget:', e);
            return [];
        }
    }, [tasks, baselines, progressHistory]);

    // Active baseline
    const activeBaseline = baselines.find(b => b.vigente);

    // Latest real progress point
    const lastRealPoint = React.useMemo(() => {
        const withReal = curveData.filter((d: any) => d.real !== null && d.real !== undefined && d.real > 0);
        return withReal.length > 0 ? withReal[withReal.length - 1] : null;
    }, [curveData]);

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Top Legend and metrics */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '6px', borderBottom: '1px solid #f1f5f9', flexShrink: 0, gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 600, color: '#475569' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                        Avance Real {lastRealPoint ? `(${truncateProgress(lastRealPoint.real)}%)` : ''}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 600, color: '#475569' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1a73e8', display: 'inline-block' }} />
                        {activeBaseline ? `Progr. Rev ${activeBaseline.versionNumber} (Vigente)` : 'Progr. Vigente'}
                    </span>
                    {baselines.filter(b => b.selectedForChart && !b.vigente).map((b, idx) => {
                        const colors = ['#f59e0b', '#ec4899', '#8b5cf6', '#3b82f6', '#ef4444'];
                        const color = colors[idx % colors.length];
                        return (
                            <span key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 600, color: '#94a3b8' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, display: 'inline-block' }} />
                                Rev {b.versionNumber}
                            </span>
                        );
                    })}
                </div>
            </div>

            {/* S-Curve Chart Canvas */}
            <div style={{ flex: 1, minHeight: 0, width: '100%', marginTop: '6px' }}>
                {curveData.length === 0 ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '11px' }}>
                        No hay suficientes datos de planificación para trazar la Curva S
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={curveData} margin={{ top: 10, right: 15, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorRealPres" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 9, fill: '#94a3b8' }}
                                minTickGap={20}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 9, fill: '#94a3b8' }}
                                unit="%"
                                domain={[0, 100]}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: '#1e293b',
                                    border: '1px solid #334155',
                                    borderRadius: '8px',
                                    color: '#fff',
                                    fontSize: '11px',
                                    padding: '6px 10px'
                                }}
                            />
                            {/* Baseline activa */}
                            <Area
                                type="monotone"
                                dataKey="planActive"
                                stroke="#1a73e8"
                                strokeWidth={2.5}
                                fill="transparent"
                                name="Progr. Vigente"
                            />
                            {/* Baselines de comparación */}
                            {baselines.filter(b => b.selectedForChart && !b.vigente).map((b, idx) => {
                                const colors = ['#f59e0b', '#ec4899', '#8b5cf6', '#3b82f6', '#ef4444'];
                                const color = colors[idx % colors.length];
                                return (
                                    <Area
                                        key={b.id}
                                        type="monotone"
                                        dataKey={`plan_rev${b.versionNumber}`}
                                        stroke={color}
                                        strokeWidth={1.5}
                                        strokeDasharray="4 4"
                                        fill="transparent"
                                        strokeOpacity={0.5}
                                        name={`Rev ${b.versionNumber}`}
                                    />
                                );
                            })}
                            {/* Avance real acumulado */}
                            <Area
                                type="monotone"
                                dataKey="real"
                                stroke="#10b981"
                                strokeWidth={2.5}
                                fillOpacity={1}
                                fill="url(#colorRealPres)"
                                name="Avance Real"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

// =========================================================================
// WIDGET REAL: EVALUACIÓN DE SUBCONTRATOS (SC)
// =========================================================================
export const SCEvaluationPresentationWidget: React.FC<{ weeklyPlanData?: WeeklyPlanDataProps }> = ({ weeklyPlanData }) => {
    const weeklyPlans = (weeklyPlanData?.weeklyPlans || []) as WeeklyPlan[];
    const tasks = weeklyPlanData?.tasks || [];
    const subcontractors = (weeklyPlanData?.subcontractors || []) as Subcontractor[];
    const contracts = (weeklyPlanData?.contracts || []) as any[];
    const savedProtocols = weeklyPlanData?.savedProtocols || {};
    const lookaheadPlans = weeklyPlanData?.lookaheadPlans || [];
    const globalUsers = weeklyPlanData?.globalUsers || [];
    const responsibles = weeklyPlanData?.responsibles || [];
    const activePlanId = weeklyPlanData?.activePlanId;

    // Load evaluation config (areas, parameters, ranges)
    const evaluationConfig = React.useMemo(() => {
        if (weeklyPlanData?.evaluationConfig && weeklyPlanData.evaluationConfig.areas?.length > 0) {
            return weeklyPlanData.evaluationConfig;
        }
        try {
            const saved = localStorage.getItem('klydapp_sc_evaluation_config');
            if (saved) return JSON.parse(saved);
        } catch {}
        return { areas: [], ranges: DEFAULT_EVALUATION_RANGES };
    }, [weeklyPlanData?.evaluationConfig]);

    const areas: any[] = evaluationConfig.areas || [];
    const ranges: any[] = evaluationConfig.ranges || DEFAULT_EVALUATION_RANGES;

    // Determine target plan
    const currentPlan = React.useMemo(() => {
        if (!weeklyPlans || weeklyPlans.length === 0) return null;
        const sorted = [...weeklyPlans].sort((a, b) => a.id - b.id);
        if (activePlanId) {
            const found = sorted.find(p => p.id === activePlanId);
            if (found) return found;
        }
        return sorted.find(p => p.status !== 'Cerrada') || sorted[sorted.length - 1];
    }, [weeklyPlans, activePlanId]);

    // Active subcontractors (filtering active status)
    const activeSubcontractors = React.useMemo(() => {
        const list = subcontractors.filter(s => (s.status || 'Activo') === 'Activo');
        if (list.length > 0) return list;
        // Fallback demo contractors if project has no subcontractors configured yet
        return [
            { id: 'sc-1', razonSocial: 'JUAN GABRIEL URIBE ULE', rut: '17.595.008-7' },
            { id: 'sc-2', razonSocial: 'ALFONSO BARRERA GUERR...', rut: '77.996.594-5' }
        ] as any[];
    }, [subcontractors]);

    // Read stored manual evaluations
    const evaluations = React.useMemo<Record<string, Record<string, Record<string, number>>>>(() => {
        try {
            const storageKey = `klydapp_sc_evaluations_${weeklyPlanData?.projectInfo?.name || 'default'}`;
            const saved = localStorage.getItem(storageKey);
            if (saved) return JSON.parse(saved);
        } catch (e) {
            console.error('Error loading evaluations for presentation widget:', e);
        }
        return {};
    }, [weeklyPlanData?.projectInfo?.name]);

    // Fallback standard areas if no configuration is present in system
    const effectiveAreas = React.useMemo(() => {
        if (areas.length > 0) return areas;
        return [
            {
                id: 'area-plazo',
                name: 'Plazo',
                weight: 25,
                parameters: [
                    { id: 'p-ppc', name: 'PPC', weight: 70, indicatorType: 'Automatico', automaticCode: 'PPC' },
                    { id: 'p-cnc', name: 'CNC', weight: 30, indicatorType: 'Automatico', automaticCode: 'CNC' }
                ]
            },
            {
                id: 'area-calidad',
                name: 'Calidad',
                weight: 25,
                parameters: [
                    { id: 'p-conf', name: 'Confiabilidad', weight: 100, indicatorType: 'Automatico', automaticCode: 'CONFIABILIDAD' }
                ]
            },
            {
                id: 'area-seguridad',
                name: 'Seguridad',
                weight: 25,
                parameters: [
                    { id: 'p-pac', name: 'PAC', weight: 50, indicatorType: 'Manual' },
                    { id: 'p-amon', name: 'Amonestaciones', weight: 15, indicatorType: 'Manual' },
                    { id: 'p-acc', name: 'Accidentabilidad', weight: 35, indicatorType: 'Manual' }
                ]
            },
            {
                id: 'area-compromiso',
                name: 'Compromiso y Gestión',
                weight: 5,
                parameters: [
                    { id: 'p-pcr', name: 'PCR (Restricciones)', weight: 60, indicatorType: 'Automatico', automaticCode: 'PCR' },
                    { id: 'p-part', name: 'Participación', weight: 40, indicatorType: 'Manual' }
                ]
            },
            {
                id: 'area-admin',
                name: 'Aspectos Administrativos',
                weight: 10,
                parameters: [
                    { id: 'p-leyes', name: 'Certificación leyes sociales', weight: 100, indicatorType: 'Manual' }
                ]
            },
            {
                id: 'area-sost',
                name: 'Sostenibilidad',
                weight: 10,
                parameters: [
                    { id: 'p-amb-pac', name: 'Ambiental (PAC)', weight: 45, indicatorType: 'Manual' },
                    { id: 'p-amb-doc', name: 'Ambiental (Documentación)', weight: 55, indicatorType: 'Manual' }
                ]
            }
        ];
    }, [areas]);

    // Helper: Find assigned users for subcontractor
    const getSubcontractorUsers = (sub: Subcontractor) => {
        const subRazonNorm = (sub.razonSocial || '').trim().toLowerCase();
        const linkedSet = new Set(sub.linkedUserIds || []);
        const matchedMap = new Map<string, { id: string; name: string }>();

        (globalUsers || []).forEach((u: any) => {
            if (!u || u.status === 'Inactive') return;
            const uComp = (u.companyName || '').trim().toLowerCase();
            const matchesComp = Boolean(uComp && subRazonNorm && (uComp === subRazonNorm || uComp.includes(subRazonNorm) || subRazonNorm.includes(uComp)));
            const matchesLinked = linkedSet.has(u.id);
            if (matchesComp || matchesLinked) {
                matchedMap.set(u.id, { id: u.id, name: u.name });
            }
        });

        (responsibles || []).forEach((r: any) => {
            if (!r) return;
            const rComp = (r.companyName || '').trim().toLowerCase();
            const matchesComp = Boolean(rComp && subRazonNorm && (rComp === subRazonNorm || rComp.includes(subRazonNorm) || subRazonNorm.includes(rComp)));
            const matchesLinked = linkedSet.has(r.id);
            const matchesSubId = r.id === sub.id;
            if (matchesComp || matchesLinked || matchesSubId) {
                if (!matchedMap.has(r.id)) {
                    matchedMap.set(r.id, { id: r.id, name: r.name });
                }
            }
        });

        return Array.from(matchedMap.values());
    };

    // Auto-calculate metric (PPC, CNC, Confiabilidad, PCR)
    const calculateAutoMetric = (param: any, sub: any, plan: WeeklyPlan | null): number | null => {
        if (!plan) return null;
        const assignedUsers = getSubcontractorUsers(sub);
        const assignedUserIds = new Set(assignedUsers.map(u => u.id));

        const subContract = contracts.find((c: any) => c.subcontractorId === sub.id);
        const subTaskIds = new Set<string>();
        if (subContract?.items) {
            subContract.items.forEach((it: any) => { if (it.id) subTaskIds.add(it.id); });
        }
        tasks.forEach(t => {
            if (t.responsibleId === sub.id || assignedUserIds.has(t.responsibleId) || (subContract?.items && subContract.items.some((it: any) => it.id === t.id))) {
                subTaskIds.add(t.id);
            }
        });

        if (param.automaticCode === 'PPC') {
            const userPpcValues: number[] = [];
            assignedUsers.forEach(u => {
                const userAllocations = (plan.allocations || []).filter(a =>
                    a.responsibleId === u.id || (subTaskIds.has(a.taskId) && a.responsibleId === sub.id)
                );
                if (userAllocations.length > 0) {
                    const valid = userAllocations.filter(a => a.executed !== null);
                    const list = valid.length > 0 ? valid : userAllocations;
                    const exec = list.filter(a => a.executed === true).length;
                    userPpcValues.push((exec / list.length) * 100);
                }
            });
            if (userPpcValues.length > 0) {
                return Math.round((userPpcValues.reduce((a, b) => a + b, 0) / userPpcValues.length) * 10) / 10;
            }
            const subAllocations = (plan.allocations || []).filter(a =>
                a.responsibleId === sub.id || (subTaskIds.size > 0 && subTaskIds.has(a.taskId))
            );
            if (subAllocations.length > 0) {
                const valid = subAllocations.filter(a => a.executed !== null);
                const list = valid.length > 0 ? valid : subAllocations;
                const exec = list.filter(a => a.executed === true).length;
                return Math.round(((exec / list.length) * 100) * 10) / 10;
            }
            return null;
        }

        if (param.automaticCode === 'PCR') {
            const weekStartDate = (plan.startDate || '').substring(0, 10);
            const weekEndDate = (plan.endDate || '').substring(0, 10);
            const planIdStr = String(plan.id);

            const allConstraints: any[] = [];
            (lookaheadPlans || []).forEach((lp: any) => {
                (lp.constraints || []).forEach((c: any) => {
                    const reqDate = (c.requiredDate || '').substring(0, 10);
                    const isInWeek = (reqDate && weekStartDate && weekEndDate && reqDate >= weekStartDate && reqDate <= weekEndDate) ||
                                     (c.originPlanId && String(c.originPlanId) === planIdStr);
                    if (isInWeek) {
                        allConstraints.push(c);
                    }
                });
            });
            const subConstraints = allConstraints.filter(c =>
                c.responsibleId === sub.id || (c.responsible && (c.responsible === sub.razonSocial || c.responsible.trim().toLowerCase() === (sub.razonSocial || '').trim().toLowerCase()))
            );
            if (subConstraints.length > 0) {
                const lib = subConstraints.filter(c => {
                    const s = (c.status || '').toLowerCase();
                    return s === 'liberado' || s === 'liberada' || s.includes('liber');
                }).length;
                return Math.round(((lib / subConstraints.length) * 100) * 10) / 10;
            }
            return null;
        }

        if (param.automaticCode === 'CONFIABILIDAD') {
            const weekStartDate = (plan.startDate || '').substring(0, 10);
            const weekEndDate = (plan.endDate || '').substring(0, 10);

            const allProtocols = Object.values(savedProtocols as Record<string, any>).filter(sp => {
                if (!sp || sp.isDeleted) return false;
                if (sp.history && Array.isArray(sp.history) && sp.history.length > 0) {
                    return sp.history.some((h: any) => {
                        const hDate = (h.date || '').substring(0, 10);
                        return (!weekStartDate || hDate >= weekStartDate) && (!weekEndDate || hDate <= weekEndDate);
                    });
                }
                if (sp.taskId && subTaskIds.has(sp.taskId)) {
                    return (plan.allocations || []).some(a => a.taskId === sp.taskId);
                }
                return false;
            });
            const subProtocols = allProtocols.filter(sp => sp.taskId && subTaskIds.has(sp.taskId));
            if (subProtocols.length > 0) {
                const approved = subProtocols.filter(sp => {
                    const s = (sp.status || '').toLowerCase();
                    return s === 'cerrado' || s === 'resuelto' || s === 'aprobado';
                }).length;
                return Math.round(((approved / subProtocols.length) * 100) * 10) / 10;
            }
            return null;
        }

        if (param.automaticCode === 'CNC') {
            const subAllocations = (plan.allocations || []).filter(a =>
                assignedUserIds.has(a.responsibleId) || a.responsibleId === sub.id || (subTaskIds.size > 0 && subTaskIds.has(a.taskId))
            );
            if (subAllocations.length === 0) return null;
            const cncCount = subAllocations.filter(a => a.cncId || a.executed === false).length;
            if (cncCount === 0) return 100;
            if (cncCount <= 5) return 70;
            if (cncCount <= 10) return 50;
            return 10;
        }

        return 100;
    };

    // Get score for cell
    const getCellScore = (planId: number | null, subId: string, param: any): number | null => {
        if (param.indicatorType === 'Automatico') {
            const targetSub = activeSubcontractors.find(s => s.id === subId) || { id: subId, razonSocial: '', rut: '' };
            return calculateAutoMetric(param, targetSub, currentPlan);
        }
        if (planId) {
            const val = evaluations[String(planId)]?.[subId]?.[param.id];
            if (val !== undefined) return val;
        }
        return 100; // Manual default
    };

    // Calculate total weighted score for subcontractor
    const calculateSubTotal = (subId: string): number | null => {
        let totalScore = 0;
        let totalEffectiveWeight = 0;
        let hasValid = false;

        effectiveAreas.forEach(area => {
            const areaWeight = Number(area.weight) || 0;
            if (areaWeight <= 0) return;
            let sumParam = 0;
            let weightParam = 0;

            (area.parameters || []).forEach((param: any) => {
                const score = getCellScore(currentPlan?.id || null, subId, param);
                const pWeight = Number(param.weight) || 0;
                if (score !== null && !isNaN(score) && pWeight > 0) {
                    sumParam += score * pWeight;
                    weightParam += pWeight;
                    hasValid = true;
                }
            });

            if (weightParam > 0) {
                totalScore += (sumParam / weightParam) * areaWeight;
                totalEffectiveWeight += areaWeight;
            }
        });

        if (!hasValid || totalEffectiveWeight === 0) return null;
        return Math.round((totalScore / totalEffectiveWeight) * 10) / 10;
    };

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Header info badge */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '10px', fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Evaluación Semanal de Subcontratistas
                    </span>
                    {currentPlan && (
                        <span style={{ fontSize: '9.5px', background: '#eff6ff', color: '#1d4ed8', padding: '1px 6px', borderRadius: '4px', fontWeight: 700, border: '1px solid #bfdbfe' }}>
                            Semana #{currentPlan.id} ({currentPlan.startDate} al {currentPlan.endDate})
                        </span>
                    )}
                </div>
            </div>

            {/* Scrollable Table */}
            <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#f8fafc' }}>
                        <tr style={{ borderBottom: '1px solid #cbd5e1' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 800, color: '#334155', textTransform: 'uppercase', width: '110px', borderRight: '1px solid #e2e8f0' }}>
                                Área
                            </th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 800, color: '#334155', textTransform: 'uppercase', minWidth: '160px', borderRight: '1px solid #e2e8f0' }}>
                                Parámetro de Evaluación
                            </th>
                            {activeSubcontractors.map(sub => (
                                <th key={sub.id} style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 800, color: '#1e293b', borderRight: '1px solid #e2e8f0', minWidth: '130px' }}>
                                    <div style={{ fontSize: '10px', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px', margin: '0 auto' }} title={sub.razonSocial}>
                                        {sub.razonSocial}
                                    </div>
                                    <div style={{ fontSize: '8.5px', color: '#94a3b8', fontWeight: 500 }}>
                                        RUT: {sub.rut || 'S/RUT'}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {effectiveAreas.map(area => (
                            <React.Fragment key={area.id}>
                                {(area.parameters || []).map((param: any, pIdx: number) => (
                                    <tr key={param.id} style={{ borderBottom: '1px solid #f1f5f9', background: '#ffffff' }}>
                                        {/* Col 1: Area (RowSpan on first param) */}
                                        {pIdx === 0 && (
                                            <td
                                                rowSpan={area.parameters.length}
                                                style={{
                                                    padding: '6px 8px',
                                                    verticalAlign: 'top',
                                                    borderRight: '1px solid #e2e8f0',
                                                    background: '#fbfcfd'
                                                }}
                                            >
                                                <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '10px' }}>
                                                    {area.name}
                                                </div>
                                                <div style={{ fontSize: '8.5px', fontWeight: 700, color: '#2563eb', marginTop: '1px' }}>
                                                    Pond: {area.weight}%
                                                </div>
                                            </td>
                                        )}

                                        {/* Col 2: Param name and badge */}
                                        <td style={{ padding: '4px 8px', borderRight: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                                                <span style={{ fontWeight: 700, color: '#334155' }}>
                                                    {param.name}
                                                </span>
                                                <span className="material-symbols-outlined" style={{ fontSize: '12px', color: '#94a3b8' }}>info</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '1px' }}>
                                                <span style={{ fontSize: '8px', color: '#64748b', fontWeight: 600 }}>Peso: {param.weight}%</span>
                                                <span style={{
                                                    fontSize: '7.5px',
                                                    fontWeight: 800,
                                                    padding: '0.5px 4px',
                                                    borderRadius: '3px',
                                                    background: param.indicatorType === 'Automatico' ? '#f3e8ff' : '#f1f5f9',
                                                    color: param.indicatorType === 'Automatico' ? '#7e22ce' : '#475569',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {param.indicatorType === 'Automatico' ? `Auto (${param.automaticCode || 'AUTO'})` : 'Manual'}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Subcontractor Score Cells */}
                                        {activeSubcontractors.map(sub => {
                                            const score = getCellScore(currentPlan?.id || null, sub.id, param);
                                            const matchedRange = score !== null ? getEvaluationRangeForScore(score, ranges) : null;

                                            return (
                                                <td key={sub.id} style={{ padding: '3px 6px', textAlign: 'center', borderRight: '1px solid #e2e8f0' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                        {param.indicatorType === 'Manual' ? (
                                                            <span style={{
                                                                display: 'inline-block',
                                                                width: '36px',
                                                                padding: '1px 2px',
                                                                background: '#f8fafc',
                                                                border: '1px solid #cbd5e1',
                                                                borderRadius: '4px',
                                                                fontSize: '9.5px',
                                                                fontWeight: 700,
                                                                color: '#1e293b'
                                                            }}>
                                                                {score ?? 100}
                                                            </span>
                                                        ) : score !== null ? (
                                                            <div style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '2px',
                                                                background: '#fdf4ff',
                                                                border: '1px solid #f0abfc',
                                                                padding: '1px 5px',
                                                                borderRadius: '4px',
                                                                fontSize: '9px',
                                                                fontWeight: 800,
                                                                color: '#86198f'
                                                            }}>
                                                                <span className="material-symbols-outlined" style={{ fontSize: '10px' }}>auto_awesome</span>
                                                                <span>{score}%</span>
                                                            </div>
                                                        ) : (
                                                            <div style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '2px',
                                                                background: '#f1f5f9',
                                                                border: '1px solid #e2e8f0',
                                                                padding: '1px 5px',
                                                                borderRadius: '4px',
                                                                fontSize: '9px',
                                                                fontWeight: 700,
                                                                color: '#94a3b8'
                                                            }}>
                                                                <span className="material-symbols-outlined" style={{ fontSize: '10px' }}>auto_awesome</span>
                                                                <span>N/A</span>
                                                            </div>
                                                        )}

                                                        {/* Badge Icon */}
                                                        {score !== null && matchedRange && (
                                                            <span
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '16px',
                                                                    height: '16px',
                                                                    borderRadius: '50%',
                                                                    background: `${matchedRange.color || '#16a34a'}15`,
                                                                    color: matchedRange.color || '#16a34a'
                                                                }}
                                                                title={`${matchedRange.level} (${score}%)`}
                                                            >
                                                                <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>
                                                                    {matchedRange.icon || 'sentiment_satisfied'}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}

                        {/* FINAL ROW: PROMEDIO / NOTA FINAL SUBCONTRATO */}
                        <tr style={{ background: '#f8fafc', borderTop: '2px solid #94a3b8', position: 'sticky', bottom: 0, zIndex: 10 }}>
                            <td colSpan={2} style={{ padding: '8px 10px', fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px', borderRight: '1px solid #cbd5e1' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px', color: '#16a34a' }}>workspace_premium</span>
                                    <span>Promedio / Nota Final Subcontrato</span>
                                </div>
                            </td>
                            {activeSubcontractors.map(sub => {
                                const total = calculateSubTotal(sub.id);
                                const matchedRange = total !== null ? getEvaluationRangeForScore(total, ranges) : null;

                                return (
                                    <td key={sub.id} style={{ padding: '6px 8px', textAlign: 'center', borderRight: '1px solid #cbd5e1' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                            <span style={{ fontSize: '12px', fontWeight: 900, color: '#0f172a' }}>
                                                {total !== null ? `${total}%` : 'N/A'}
                                            </span>
                                            {total !== null && matchedRange ? (
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '3px',
                                                        padding: '1px 6px',
                                                        borderRadius: '10px',
                                                        fontSize: '8.5px',
                                                        fontWeight: 800,
                                                        background: `${matchedRange.color || '#16a34a'}18`,
                                                        border: `1px solid ${matchedRange.color || '#16a34a'}40`,
                                                        color: matchedRange.color || '#16a34a'
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>
                                                        {matchedRange.icon || 'sentiment_satisfied'}
                                                    </span>
                                                    <span>{matchedRange.level}</span>
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: '8px', color: '#94a3b8', fontStyle: 'italic' }}>Sin datos</span>
                                            )}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ParetoMock = () => {
    const data = [40, 30, 20, 10];
    const maxH = 50;
    return (
        <div style={{ height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '6px', padding: '8px 0' }}>
            {data.map((v, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{ width: '18px', height: `${(v / 40) * maxH}px`, background: `hsl(${0 + i * 30}, 70%, 55%)`, borderRadius: '3px 3px 0 0' }} />
                    <div style={{ fontSize: '8px', color: '#64748b', marginTop: '2px' }}>{`C${i + 1}`}</div>
                </div>
            ))}
        </div>
    );
};

const PPCEvolutionMock = () => (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <svg viewBox="0 0 120 50" style={{ width: '100%', maxHeight: '100%' }}>
            <polyline points="5,40 25,35 45,28 65,30 85,22 105,18" fill="none" stroke="#3b82f6" strokeWidth="2" />
            <polyline points="5,42 25,38 45,35 65,33 85,30 105,28" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4,2" />
            {[5, 25, 45, 65, 85, 105].map((x, i) => (
                <circle key={i} cx={x} cy={[40, 35, 28, 30, 22, 18][i]} r="2.5" fill="#3b82f6" />
            ))}
        </svg>
    </div>
);

const KPICardsMock = () => (
    <div style={{ height: '100%', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', padding: '4px' }}>
        {[
            { label: 'PPC', value: '78%', color: '#3b82f6', icon: 'speed' },
            { label: 'PCR', value: '85%', color: '#8b5cf6', icon: 'verified' },
            { label: 'Avance', value: '62%', color: '#059669', icon: 'trending_up' },
            { label: 'Atraso', value: '3d', color: '#ef4444', icon: 'schedule' },
        ].map((kpi, i) => (
            <div key={i} style={{
                background: `${kpi.color}08`, border: `1px solid ${kpi.color}25`, borderRadius: '6px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4px',
            }}>
                <span className="material-symbols-outlined" style={{ fontSize: '14px', color: kpi.color }}>{kpi.icon}</span>
                <div style={{ fontSize: '13px', fontWeight: 800, color: kpi.color }}>{kpi.value}</div>
                <div style={{ fontSize: '8px', color: '#64748b' }}>{kpi.label}</div>
            </div>
        ))}
    </div>
);

/**
 * Renders the layout and widgets of a report template inside a 16:9 slide canvas.
 */
const ReportTemplateSlideView: React.FC<{ config?: Record<string, any> }> = ({ config }) => {
    const templateName = config?.templateName || 'Plantilla de Reporte';
    const pageName = config?.pageName;
    const totalPages = config?.totalPages || 1;
    const pageIndex = config?.pageIndex !== undefined ? config.pageIndex + 1 : 1;
    const widgets: any[] = config?.widgets || [];

    return (
        <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            padding: '12px 16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {/* Template Page Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingBottom: '8px',
                borderBottom: '1.5px solid #f1f5f9',
                flexShrink: 0
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, #e11d48, #be123c)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff'
                    }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>analytics</span>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.2px' }}>
                            {templateName}
                        </div>
                        {pageName && (
                            <div style={{ fontSize: '9.5px', fontWeight: 600, color: '#64748b' }}>
                                {pageName}
                            </div>
                        )}
                    </div>
                </div>
                {totalPages > 1 && (
                    <div style={{
                        fontSize: '9px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: '12px',
                        background: '#ffe4e6',
                        color: '#e11d48',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>layers</span>
                        Pestaña {pageIndex} de {totalPages}
                    </div>
                )}
            </div>

            {/* Template Widgets Grid (12 cols x 6 rows) */}
            <div style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: 'repeat(12, 1fr)',
                gridTemplateRows: 'repeat(6, 1fr)',
                gap: '8px',
                minHeight: 0,
                overflow: 'hidden'
            }}>
                {widgets.length === 0 ? (
                    <div style={{
                        gridColumn: 'span 12',
                        gridRow: 'span 6',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#94a3b8',
                        background: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px dashed #cbd5e1'
                    }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '32px', color: '#cbd5e1' }}>dashboard_customize</span>
                        <span style={{ fontSize: '11px', fontWeight: 600, marginTop: '4px' }}>Lienzo de reporte libre</span>
                    </div>
                ) : (
                    widgets.map((w: any, idx: number) => {
                        const colStart = w.gridColStart || 1;
                        const colSpan = w.colSpan || 4;
                        const rowStart = w.gridRowStart || 1;
                        const rowSpan = w.rowSpan || 2;

                        return (
                            <div
                                key={w.id || idx}
                                style={{
                                    gridColumn: `${colStart} / span ${colSpan}`,
                                    gridRow: `${rowStart} / span ${rowSpan}`,
                                    background: '#ffffff',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                    minHeight: 0
                                }}
                            >
                                <ReportItemRenderer item={w} />
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

/**
 * Renders an individual widget within the report template slide.
 */
const ReportItemRenderer: React.FC<{ item: any }> = ({ item }) => {
    const type = item.type;
    const style = item.style || {};

    if (type === 'header-banner') {
        return (
            <div style={{
                height: '100%',
                background: style.bannerGradient ? 'linear-gradient(135deg, #1e3a8a, #2563eb)' : '#1e3a8a',
                color: '#ffffff',
                padding: '8px 14px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}>
                <div style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                    {style.bannerTitle || 'INFORME DE GESTIÓN Y PRODUCCIÓN'}
                </div>
                {style.bannerSubtitle && (
                    <div style={{ fontSize: '9px', opacity: 0.85, marginTop: '2px' }}>
                        {style.bannerSubtitle}
                    </div>
                )}
            </div>
        );
    }

    if (type === 'kpi-card') {
        const title = style.customTitle || item.valueFields?.[0]?.fieldKey || 'Indicador';
        const primaryColor = style.primaryColor || '#2563eb';
        return (
            <div style={{
                height: '100%',
                padding: '8px 10px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                background: `${primaryColor}06`,
                borderLeft: `3px solid ${primaryColor}`
            }}>
                <div style={{ fontSize: '9.5px', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {title}
                </div>
                <div style={{ fontSize: '18px', fontWeight: 900, color: primaryColor, margin: '2px 0' }}>
                    {title.includes('Real') ? '68.4%' : title.includes('Prog') ? '72.0%' : title.includes('PPC') ? '81.5%' : '24'}
                </div>
                <div style={{ fontSize: '8px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '10px', color: '#10b981' }}>trending_up</span>
                    <span>Actualizado semanal</span>
                </div>
            </div>
        );
    }

    if (type === 'image') {
        return (
            <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8fafc',
                padding: '8px',
                textAlign: 'center'
            }}>
                <span className="material-symbols-outlined" style={{ fontSize: '28px', color: '#94a3b8' }}>photo_camera</span>
                <span style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', marginTop: '4px' }}>
                    {style.customTitle || 'Registro Fotográfico'}
                </span>
                <span style={{ fontSize: '8px', color: '#94a3b8' }}>
                    {style.imageCaption || 'Inspección en terreno'}
                </span>
            </div>
        );
    }

    if (type === 'slicer') {
        return (
            <div style={{ height: '100%', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ fontSize: '8.5px', fontWeight: 700, color: '#64748b' }}>
                    {style.customTitle || 'Filtrar por Especialidad'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                    {['Todos', 'Hormigón', 'Terminaciones', 'Instalaciones'].map((s, i) => (
                        <span key={i} style={{
                            fontSize: '8px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: i === 0 ? '#2563eb' : '#f1f5f9',
                            color: i === 0 ? '#fff' : '#475569',
                            fontWeight: 600
                        }}>
                            {s}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    // Default: Graphic Chart Mock (Column, Bar, Donut, Line, etc.)
    const title = style.customTitle || (type === 'donut' || type === 'pie-solid' ? 'Distribución' : 'Comparativa Semanal');
    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '6px 8px' }}>
            <div style={{
                fontSize: '9.5px',
                fontWeight: 700,
                color: '#334155',
                marginBottom: '4px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }}>
                {title}
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {type === 'donut' || type === 'pie-solid' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '50%',
                            background: 'conic-gradient(#2563eb 0% 45%, #10b981 45% 75%, #f59e0b 75% 100%)'
                        }} />
                        <div style={{ fontSize: '7.5px', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <span style={{ width: '6px', height: '6px', background: '#2563eb', borderRadius: '1px' }} /> Conforme (45%)
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <span style={{ width: '6px', height: '6px', background: '#10b981', borderRadius: '1px' }} /> En Proceso (30%)
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <span style={{ width: '6px', height: '6px', background: '#f59e0b', borderRadius: '1px' }} /> Observado (25%)
                            </div>
                        </div>
                    </div>
                ) : type === 'bar-grouped-horizontal' ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: '2px' }}>
                        {[
                            { name: 'Obra Gruesa', val: '88%', color: '#2563eb' },
                            { name: 'Instalaciones', val: '72%', color: '#0ea5e9' },
                            { name: 'Terminaciones', val: '64%', color: '#6366f1' },
                        ].map((b, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '8px' }}>
                                <span style={{ width: '55px', color: '#64748b', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{b.name}</span>
                                <div style={{ flex: 1, background: '#f1f5f9', height: '8px', borderRadius: '2px', overflow: 'hidden' }}>
                                    <div style={{ width: b.val, height: '100%', background: b.color }} />
                                </div>
                                <span style={{ fontWeight: 700, color: '#334155', width: '22px' }}>{b.val}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <svg viewBox="0 0 160 60" style={{ width: '100%', height: '100%', maxHeight: '65px' }}>
                        {/* Bars / Curve */}
                        <rect x="15" y="25" width="10" height="30" fill="#93c5fd" rx="2" />
                        <rect x="27" y="20" width="10" height="35" fill="#2563eb" rx="2" />

                        <rect x="50" y="20" width="10" height="35" fill="#93c5fd" rx="2" />
                        <rect x="62" y="15" width="10" height="40" fill="#2563eb" rx="2" />

                        <rect x="85" y="15" width="10" height="40" fill="#93c5fd" rx="2" />
                        <rect x="97" y="10" width="10" height="45" fill="#2563eb" rx="2" />

                        <rect x="120" y="12" width="10" height="43" fill="#93c5fd" rx="2" />
                        <rect x="132" y="5" width="10" height="50" fill="#2563eb" rx="2" />

                        {/* S-Curve line */}
                        <path d="M 20 50 Q 75 35 137 15" fill="none" stroke="#ef4444" strokeWidth="2" strokeDasharray="3,2" />
                    </svg>
                )}
            </div>
        </div>
    );
};

export default WidgetRenderer;
